
import { defineComponent } from 'vue'
import { VCodeMirror } from 'vue3-code-mirror'
import { AxiosResponse } from 'axios'
import Chips from 'primevue/chips'
import { downloadDirect } from '@/helpers/commons/fileHelper'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import Textarea from 'primevue/textarea'
import galleryDescriptor from './GalleryManagementDescriptor.json'
import { IGalleryTemplate } from './GalleryManagement'
import useValidate from '@vuelidate/core'
import { createValidations } from '@/helpers/commons/validationHelper'
import KnValidationMessages from '@/components/UI/KnValidatonMessages.vue'

export default defineComponent({
    name: 'gallery-management-detail',
    components: {
        Chips,
        VCodeMirror,
        Dropdown,
        KnValidationMessages,
        InputText,
        TabView,
        TabPanel,
        Textarea
    },
    props: {
        id: String
    },
    emits: ['saved'],
    data() {
        return {
            dirty: false as boolean,
            files: [],
            loading: false as boolean,
            test: '' as string,
            galleryTemplates: [],
            template: {} as IGalleryTemplate,
            galleryDescriptor: galleryDescriptor,
            windowWidth: window.innerWidth,
            windowWidthBreakPoint: 1500,
            v$: useValidate() as any
        }
    },
    validations() {
        return {
            template: createValidations('template', this.galleryDescriptor.validations.template)
        }
    },
    computed: {
        isSaveButtonDisabled(): any {
            return this.v$.$invalid || !this.dirty
        }
    },
    watch: {
        '$route.params.id': function(id) {
            this.loadTemplate(id)
        }
    },
    created() {
        this.loadTemplate(this.id)
        window.addEventListener('resize', this.resizeHandler)
    },
    unmounted() {
        window.removeEventListener('resize', this.resizeHandler)
    },
    methods: {
        downloadTemplate(): void {
            if (this.dirty) {
                this.$confirm.require({
                    message: this.$t('managers.widgetGallery.templateIsNotSaved'),
                    header: this.$t('managers.widgetGallery.downloadTemplate'),
                    icon: 'pi pi-exclamation-triangle',
                    accept: () => {
                        downloadDirect(JSON.stringify(this.template), this.template.name, 'application/json')
                    }
                })
            } else {
                downloadDirect(JSON.stringify(this.template), this.template.name, 'application/json')
            }
        },
        closeTemplateConfirm(): void {
            if (!this.dirty) {
                this.closeTemplate()
            } else {
                this.$confirm.require({
                    message: this.$t('common.toast.unsavedChangesMessage'),
                    header: this.$t('common.toast.unsavedChangesHeader'),
                    icon: 'pi pi-exclamation-triangle',
                    accept: () => {
                        this.dirty = false
                        this.closeTemplate()
                    }
                })
            }
        },
        closeTemplate(): void {
            this.$router.push('/gallery-management')
        },
        loadTemplate(id?: string): void {
            this.loading = true
            if (id) {
                this.$http
                    .get(process.env.VUE_APP_API_PATH + '1.0/widgetgallery/' + (id || this.id))
                    .then((response: AxiosResponse<any>) => {
                        this.template = response.data
                    })
                    .catch((error) => console.error(error))
                    .finally(() => {
                        this.loading = false
                        this.dirty = false
                    })
            } else {
                this.template = { type: 'html', code: { html: '', css: '', javascript: '', python: '' } } as IGalleryTemplate
                this.loading = false
                this.dirty = false
            }
        },
        onCmCodeChange(): void {
            this.setDirty()
        },
        saveTemplate(): void {
            if (this.validateTags()) {
                const postUrl = this.id ? '1.0/widgetgallery/' + this.id : '1.0/widgetgallery'
                const label = this.template.label
                const name = this.template.name
                if (!label && !name) {
                    this.$store.commit('setError', { title: this.$t('common.error.uploading'), msg: this.$t('managers.widgetGallery.fieldIsMandatory', { field: this.$t('common.name') }) })
                    return
                }
                if (!label && name) this.template.label = name
                if (label && !name) this.template.name = label
                this.$http
                    .post(process.env.VUE_APP_API_PATH + postUrl, this.template)
                    .then((response: AxiosResponse<any>) => {
                        this.$store.commit('setInfo', { title: this.$t('managers.widgetGallery.saveTemplate'), msg: this.$t('managers.widgetGallery.templateSuccessfullySaved') })
                        this.$router.push('/gallery-management/' + response.data.id)
                        this.$emit('saved')
                    })
                    .catch((error) => console.error(error))
            }
        },
        setDirty(): void {
            this.dirty = true
        },
        uploadFile(event): void {
            const reader = new FileReader()
            const self = this
            reader.addEventListener(
                'load',
                function() {
                    self.template.image = reader.result || ''
                },
                false
            )
            if (event.srcElement.files[0] && event.srcElement.files[0].size < process.env.VUE_APP_MAX_UPLOAD_IMAGE_SIZE) {
                reader.readAsDataURL(event.srcElement.files[0])
                this.setDirty()
            } else this.$store.commit('setError', { title: this.$t('common.error.uploading'), msg: this.$t('common.error.exceededSize', { size: '(200KB)' }) })
        },
        resizeHandler(): void {
            this.windowWidth = window.innerWidth
        },
        validateTags(): boolean {
            const validationRegex = /^([a-zA-Z0-9-_])*$/g
            for (const idx in this.template.tags) {
                const currentTag = this.template.tags[idx]
                const valid = currentTag.match(validationRegex)
                if (!valid) {
                    this.$store.commit('setError', { title: this.$t('common.error.uploading'), msg: this.$t('common.error.tags.tagIsNotValid', { tag: currentTag }) })
                    return false
                }
            }
            return true
        },
        tabChange(e) {
            let ref = 'editor_' + e.index
            // eslint-disable-next-line
            // @ts-ignore
            this.$refs[ref].editor.refresh()
        }
    }
})
